import { useCallback, useEffect, useState } from 'react';
import { clientScopedFetch } from '../lib/authedFetch';
import { useViewingAs } from '../hooks/useViewingAs';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

const DAYS = [
  ['MONDAY', 'Monday'], ['TUESDAY', 'Tuesday'], ['WEDNESDAY', 'Wednesday'], ['THURSDAY', 'Thursday'],
  ['FRIDAY', 'Friday'], ['SATURDAY', 'Saturday'], ['SUNDAY', 'Sunday'],
];
// Google's listing photo types (gbp-listing.js PHOTO_CATEGORIES), in words.
const PHOTO_TYPES = [
  ['EXTERIOR', 'Outside'], ['INTERIOR', 'Inside'], ['PRODUCT', 'Product'], ['AT_WORK', 'At work'],
  ['FOOD_AND_DRINK', 'Food & drink'], ['MENU', 'Menu'], ['TEAMS', 'Team'], ['COMMON_AREA', 'Common area'],
  ['ROOMS', 'Rooms'], ['ADDITIONAL', 'Other'], ['COVER', 'Cover photo'], ['PROFILE', 'Profile photo'],
];
const DESCRIPTION_MAX = 750;

// Date.UTC(...).getUTCDay() returns 0=Sunday..6=Saturday with no local-timezone
// shift; DAYS is Monday-first, so (dow + 6) % 7 maps Sunday to index 6.
function weekdayKeyOf(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '');
  if (!m) return null;
  const dow = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay();
  return DAYS[(dow + 6) % 7][0];
}

function formatLongDate(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '');
  if (!m) return dateStr;
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]))
    .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// A closed holiday that's new, or newly marked closed, compared to what's
// already saved on Google — these are the ones that need the client to
// confirm before we publish a "CLOSED" day. A date that was already saved
// as closed doesn't need re-confirming just because some other row changed.
function newlyClosedDates(draftSpecial, savedSpecial) {
  const savedByDate = new Map((savedSpecial || []).map(sp => [sp.date, sp]));
  return draftSpecial.filter(s => s.closed && s.date && !(savedByDate.get(s.date) || {}).closed);
}

async function readJson(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.ok === false) {
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.code = body.code;
    throw err;
  }
  return body;
}

const card = { background:'var(--bg-card2, var(--bg-surface))', border:'1px solid var(--border)', borderRadius:10, padding:16, marginBottom:16 };
const label = { fontSize:12, fontWeight:600, color:'var(--text-muted)', marginBottom:4, display:'block' };
const input = { width:'100%', boxSizing:'border-box', padding:'8px 10px', borderRadius:8, fontSize:13, background:'var(--bg-surface)', border:'1px solid var(--border)', color:'var(--text-primary)', fontFamily:'var(--font)' };
const timeInput = { ...input, width:'auto', flex:'0 0 auto' };
const btn = { padding:'8px 14px', borderRadius:8, border:'none', background:'#31DCBA', color:'#0d0f14', fontWeight:700, cursor:'pointer', fontSize:13 };

// What happened on a save, in words: applied now, or held for Google's review.
function SaveResult({ result }) {
  if (!result) return null;
  if (result.error) return <div className="cq-error" style={{ marginTop:8 }}>{result.error}</div>;
  const held = result.held_for_review || [];
  return (
    <div style={{ marginTop:8, fontSize:12, lineHeight:1.5, color: held.length ? '#f5a623' : 'var(--text-secondary)' }} data-testid="save-result">
      {held.length
        ? `Saved. Google is reviewing your ${held.join(', ').toLowerCase()} change before it shows — this can take a few days.`
        : 'Saved to Google.'}
    </div>
  );
}

// "Recent changes": one line per recorded edit (gbp-listing-history.js on the
// server). Values are shown in the same words as the form above.
const HISTORY_FIELDS = {
  phone: 'Phone', website: 'Website', description: 'Description',
  regular_hours: 'Opening hours', special_hours: 'Holiday hours', photo: 'Photo',
};
const HISTORY_STATUS = {
  applied: 'Saved to Google',
  pending_review: 'Sent for Google’s review',
  unknown: 'Sent to Google',
};
function historyValue(field, v) {
  if (v === null || v === undefined) return '(none)';
  if (field === 'phone') return [v.primary, ...(v.additional || [])].filter(Boolean).join(', ') || '(none)';
  if (field === 'website') return v || '(none)';
  if (field === 'description') return !v ? '(none)' : v.length > 80 ? `${v.slice(0, 79)}…` : v;
  if (field === 'regular_hours') {
    const open = DAYS.filter(([d]) => (v[d] || []).length)
      .map(([d, name]) => `${name.slice(0, 3)} ${v[d].map(sl => `${sl.open}–${sl.close}`).join(', ')}`);
    return open.length ? open.join('; ') : 'Closed every day';
  }
  if (field === 'special_hours') {
    return v.length ? v.map(sp => `${sp.date}${sp.end_date ? `–${sp.end_date}` : ''} ${sp.closed ? 'closed' : `${sp.open}–${sp.close}`}`).join('; ') : '(none)';
  }
  return String(v);
}

function RecentChanges({ history }) {
  if (!history || history.unavailable) return null;   // table not created yet
  return (
    <div style={card} data-testid="recent-changes">
      <h3 style={{ margin:'0 0 12px', fontSize:15, color:'var(--text-primary)' }}>Recent changes</h3>
      {history.error && <div className="cq-error">Couldn’t load recent changes.</div>}
      {!history.error && history.rows.length === 0 && <div style={{ fontSize:13, color:'var(--text-muted)' }}>No changes recorded yet.</div>}
      {history.rows.map(h => (
        <div key={h.id} data-testid="history-row" style={{ padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:12, lineHeight:1.5, color:'var(--text-secondary)' }}>
          <div style={{ color:'var(--text-muted)' }}>
            {new Date(h.created_at).toLocaleString()} · {h.acting_as ? 'Autopilot Digital, for you' : 'You'}
            {HISTORY_STATUS[h.review_status] ? ` · ${HISTORY_STATUS[h.review_status]}` : ''}
          </div>
          {h.kind === 'photo_add'
            ? <div>Photo added ({PHOTO_TYPES.find(t => t[0] === h.new_values?.category)?.[1] || 'Photo'})</div>
            : (h.fields || []).map(f => (
              <div key={f}>
                <strong style={{ color:'var(--text-primary)', fontWeight:600 }}>{HISTORY_FIELDS[f] || f}:</strong>{' '}
                {historyValue(f, h.old_values?.[f])} → {historyValue(f, h.new_values?.[f])}
              </div>
            ))}
        </div>
      ))}
    </div>
  );
}

// Google Business listing management: phone, website, description, hours,
// holiday hours, and photos on the listing itself.
//
// Every read goes through clientScopedFetch and reloads when the viewed client
// changes (viewingAsClientId) — the Phase 3 rule, so an admin viewing a client
// edits THAT client's listing. Each section saves on its own: one save is one
// edit against Google's 10-a-minute limit, and a refused edit only loses that
// section's changes.
export default function GoogleProfilePanel({ clientId }) {
  const { viewingAsClientId } = useViewingAs();
  const [listing, setListing] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(null);           // section being saved
  const [results, setResults] = useState({});       // section → SaveResult
  const [photos, setPhotos] = useState(null);
  const [photoType, setPhotoType] = useState('EXTERIOR');
  const [photoResult, setPhotoResult] = useState(null);
  const [history, setHistory] = useState(null);     // { rows, unavailable, error }
  const [specialConfirm, setSpecialConfirm] = useState(null); // dates pending the closed-day confirm

  const fromListing = (l) => ({
    phone: l.phone.primary || '',
    phone2: l.phone.additional[0] || '',
    phone3: l.phone.additional[1] || '',
    website: l.website || '',
    description: l.description || '',
    hours: Object.fromEntries(DAYS.map(([d]) => [d, (l.regular_hours[d] || []).map(s => ({ ...s }))])),
    special: (l.special_hours || []).map(s => ({ ...s })),
  });

  const loadHistory = useCallback(async () => {
    try {
      const d = await readJson(await clientScopedFetch(`${API_BASE}/api/google-business/listing/history`));
      setHistory({ rows: d.history || [], unavailable: !!d.unavailable, error: false });
    } catch {
      setHistory({ rows: [], unavailable: false, error: true });
    }
  }, []);

  const load = useCallback(async () => {
    setListing(null); setLoadError(null); setResults({});
    try {
      const d = await readJson(await clientScopedFetch(`${API_BASE}/api/google-business/listing`));
      setListing(d.listing);
      setDraft(fromListing(d.listing));
    } catch (err) {
      setLoadError({ message: err.message, reconnect: err.code === 'gbp_reconnect', comingSoon: err.code === 'gbp_not_available' });
    }
    try {
      const p = await readJson(await clientScopedFetch(`${API_BASE}/api/google-business/photos`));
      setPhotos(p.photos || []);
    } catch { setPhotos([]); }
    loadHistory();
  }, [loadHistory]);

  useEffect(() => { if (clientId) load(); }, [clientId, viewingAsClientId, load]);

  const save = async (section, changes) => {
    setBusy(section);
    setResults(r => ({ ...r, [section]: null }));
    try {
      const d = await readJson(await clientScopedFetch(`${API_BASE}/api/google-business/listing`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ changes }),
      }));
      setListing(d.listing);
      setResults(r => ({ ...r, [section]: d }));
      loadHistory();
    } catch (err) {
      setResults(r => ({ ...r, [section]: { error: err.message } }));
    } finally {
      setBusy(null);
    }
  };

  const setHours = (day, slots) => setDraft(d => ({ ...d, hours: { ...d.hours, [day]: slots } }));

  // The saved listing's hours for a weekday, only when it's a single slot —
  // split hours or a closed weekday have no one answer to guess from.
  const singleSlotFor = (weekdayKey) => {
    const slots = (listing.regular_hours || {})[weekdayKey] || [];
    return slots.length === 1 ? slots[0] : null;
  };

  const addPhoto = async (file) => {
    if (!file) return;
    setBusy('photo'); setPhotoResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('purpose', 'gbp-photo');
      const up = await readJson(await clientScopedFetch(`${API_BASE}/api/scratch-upload`, { method: 'POST', body: form }));
      const d = await readJson(await clientScopedFetch(`${API_BASE}/api/google-business/photos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sourceUrl: up.url, category: photoType }),
      }));
      setPhotos(p => [d.photo, ...(p || [])]);
      setPhotoResult({ ok: true });
      loadHistory();
    } catch (err) {
      setPhotoResult({ error: err.message });
    } finally {
      setBusy(null);
    }
  };

  if (loadError) {
    return (
      <div style={{ padding:32, maxWidth:760 }}>
        <div className="cq-error">
          {loadError.comingSoon
            ? 'Google Business Profile is coming soon — you’ll be able to edit your listing here once it’s available on your account.'
            : loadError.reconnect
              ? 'Reconnect Google Business on the Connections page to edit your listing.'
              : `Couldn’t load your Google listing: ${loadError.message}`}
        </div>
      </div>
    );
  }
  if (!listing || !draft) {
    return <div style={{ padding:32, fontSize:13, color:'var(--text-muted)' }}>Loading your Google listing…</div>;
  }

  return (
    <div style={{ padding:32, maxWidth:760 }} data-testid="google-profile">
      <div style={{ display:'flex', alignItems:'baseline', gap:12, flexWrap:'wrap', marginBottom:12 }}>
        <h2 style={{ margin:0, fontSize:18, color:'var(--text-primary)' }}>{listing.title || 'Your Google listing'}</h2>
        {listing.maps_url && <a href={listing.maps_url} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'var(--accent)' }}>View on Google Maps ↗</a>}
      </div>

      {listing.pending_fields.length > 0 && (
        <div style={{ ...card, borderColor:'rgba(245,166,35,0.4)', color:'#f5a623', fontSize:13, lineHeight:1.5 }} data-testid="pending-banner">
          Google is still reviewing changes to: <strong>{listing.pending_fields.join(', ')}</strong>. They’ll show on Google once approved.
        </div>
      )}
      {listing.google_updated_fields.length > 0 && (
        <div style={{ ...card, fontSize:13, lineHeight:1.5, color:'var(--text-secondary)' }}>
          Google itself updated: {listing.google_updated_fields.join(', ')}. What you see below is what Google is showing.
        </div>
      )}

      {/* Basics */}
      <div style={card}>
        <h3 style={{ margin:'0 0 12px', fontSize:15, color:'var(--text-primary)' }}>Contact &amp; description</h3>
        <label style={label} htmlFor="gp-phone">Main phone</label>
        <input id="gp-phone" style={input} value={draft.phone} onChange={e => setDraft(d => ({ ...d, phone: e.target.value }))} />
        <div style={{ display:'flex', gap:8, marginTop:8 }}>
          <input aria-label="Extra phone 1" style={input} placeholder="Extra phone (optional)" value={draft.phone2} onChange={e => setDraft(d => ({ ...d, phone2: e.target.value }))} />
          <input aria-label="Extra phone 2" style={input} placeholder="Extra phone (optional)" value={draft.phone3} onChange={e => setDraft(d => ({ ...d, phone3: e.target.value }))} />
        </div>
        <label style={{ ...label, marginTop:12 }} htmlFor="gp-website">Website</label>
        <input id="gp-website" style={input} placeholder="https://" value={draft.website} onChange={e => setDraft(d => ({ ...d, website: e.target.value }))} />
        <label style={{ ...label, marginTop:12 }} htmlFor="gp-desc">Description</label>
        <textarea id="gp-desc" style={{ ...input, minHeight:100, resize:'vertical' }} maxLength={DESCRIPTION_MAX} value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} />
        <div style={{ fontSize:11, color:'var(--text-muted)', textAlign:'right' }}>{draft.description.length}/{DESCRIPTION_MAX}</div>
        <button style={btn} disabled={busy === 'basics'} onClick={() => save('basics', {
          phone: { primary: draft.phone, additional: [draft.phone2, draft.phone3].filter(Boolean) },
          website: draft.website,
          description: draft.description,
        })}>{busy === 'basics' ? 'Saving…' : 'Save contact & description'}</button>
        <SaveResult result={results.basics} />
      </div>

      {/* Regular hours */}
      <div style={card}>
        <h3 style={{ margin:'0 0 12px', fontSize:15, color:'var(--text-primary)' }}>Opening hours</h3>
        {DAYS.map(([day, name]) => {
          const slots = draft.hours[day];
          const open = slots.length > 0;
          return (
            <div key={day} style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', padding:'6px 0', borderBottom:'1px solid var(--border)' }} data-testid={`hours-${day}`}>
              <div style={{ width:96, fontSize:13, color:'var(--text-primary)' }}>{name}</div>
              <label style={{ fontSize:12, color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:4 }}>
                <input type="checkbox" checked={open} aria-label={`${name} open`}
                  onChange={e => setHours(day, e.target.checked ? [{ open: '09:00', close: '17:00' }] : [])} />
                {open ? 'Open' : 'Closed'}
              </label>
              {slots.map((s, i) => (
                <span key={i} style={{ display:'flex', alignItems:'center', gap:4 }}>
                  <input type="time" aria-label={`${name} opens ${i + 1}`} style={timeInput} value={s.open}
                    onChange={e => setHours(day, slots.map((x, k) => (k === i ? { ...x, open: e.target.value } : x)))} />
                  –
                  <input type="time" aria-label={`${name} closes ${i + 1}`} style={timeInput} value={s.close === '24:00' ? '23:59' : s.close}
                    onChange={e => setHours(day, slots.map((x, k) => (k === i ? { ...x, close: e.target.value === '23:59' ? '24:00' : e.target.value } : x)))} />
                  {i > 0 && <button className="cal-post-btn cal-post-btn-ghost" aria-label={`Remove ${name} hours ${i + 1}`} onClick={() => setHours(day, slots.filter((_, k) => k !== i))}>✕</button>}
                </span>
              ))}
              {open && slots.length < 3 && (
                <button className="cal-post-btn cal-post-btn-ghost" onClick={() => setHours(day, [...slots, { open: '13:00', close: '17:00' }])}>+ hours</button>
              )}
            </div>
          );
        })}
        <div style={{ fontSize:11, color:'var(--text-muted)', margin:'8px 0', lineHeight:1.5 }}>
          A closing time earlier than the opening time means you close after midnight. 11:59 PM means open until midnight.
        </div>
        <button style={btn} disabled={busy === 'hours'} onClick={() => save('hours', { regular_hours: draft.hours })}>
          {busy === 'hours' ? 'Saving…' : 'Save opening hours'}
        </button>
        <SaveResult result={results.hours} />
      </div>

      {/* Holiday hours */}
      <div style={card}>
        <h3 style={{ margin:'0 0 12px', fontSize:15, color:'var(--text-primary)' }}>Holiday &amp; special hours</h3>
        {draft.special.length === 0 && <div style={{ fontSize:13, color:'var(--text-muted)', marginBottom:8 }}>No special hours set.</div>}
        {draft.special.map((s, i) => {
          // Any edit to a holiday row invalidates a pending closed-day warning —
          // "Yes, save" must only ever be able to save the exact draft it listed.
          const set = (patch) => { setSpecialConfirm(null); setDraft(d => ({ ...d, special: d.special.map((x, k) => (k === i ? { ...x, ...patch } : x)) })); };
          return (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', padding:'6px 0', borderBottom:'1px solid var(--border)' }} data-testid="special-row">
              <input type="date" aria-label={`Special date ${i + 1}`} style={timeInput} value={s.date || ''} onChange={e => {
                const date = e.target.value;
                if (!s.closed && !s.open && !s.close) {
                  const slot = singleSlotFor(weekdayKeyOf(date));
                  set(slot ? { date, open: slot.open, close: slot.close } : { date });
                } else {
                  set({ date });
                }
              }} />
              <label style={{ fontSize:12, color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:4 }}>
                <input type="checkbox" aria-label={`Closed all day ${i + 1}`} checked={!!s.closed}
                  onChange={e => {
                    if (e.target.checked) { set({ closed: true, open: null, close: null }); return; }
                    const slot = singleSlotFor(weekdayKeyOf(s.date));
                    set({ closed: false, open: slot ? slot.open : '', close: slot ? slot.close : '' });
                  }} />
                Closed all day
              </label>
              {!s.closed && (
                <>
                  <input type="time" aria-label={`Special opens ${i + 1}`} style={timeInput} value={s.open || ''} onChange={e => set({ open: e.target.value })} />
                  –
                  <input type="time" aria-label={`Special closes ${i + 1}`} style={timeInput} value={s.close === '24:00' ? '23:59' : (s.close || '')}
                    onChange={e => set({ close: e.target.value === '23:59' ? '24:00' : e.target.value })} />
                </>
              )}
              <button className="cal-post-btn cal-post-btn-delete" aria-label={`Remove special date ${i + 1}`}
                onClick={() => { setSpecialConfirm(null); setDraft(d => ({ ...d, special: d.special.filter((_, k) => k !== i) })); }}>✕</button>
            </div>
          );
        })}
        <div style={{ display:'flex', gap:8, marginTop:10 }}>
          <button className="cal-post-btn cal-post-btn-ghost" onClick={() => { setSpecialConfirm(null); setDraft(d => ({ ...d, special: [...d.special, { date: '', closed: false, open: '', close: '' }] })); }}>+ Add a date</button>
          <button style={btn} disabled={busy === 'special'} onClick={() => {
            const toConfirm = newlyClosedDates(draft.special, listing.special_hours);
            if (toConfirm.length) { setSpecialConfirm(toConfirm); return; }
            save('special', { special_hours: draft.special });
          }}>
            {busy === 'special' ? 'Saving…' : 'Save holiday hours'}
          </button>
        </div>
        {specialConfirm && (
          <div data-testid="special-confirm" style={{ marginTop:8, padding:12, borderRadius:8, border:'1px solid rgba(245,166,35,0.4)', background:'rgba(245,166,35,0.08)' }}>
            {specialConfirm.map(s => (
              <div key={s.date} style={{ fontSize:13, color:'#f5a623', marginBottom:4 }}>
                Google will show your business as CLOSED all day on {formatLongDate(s.date)}. Is that right?
              </div>
            ))}
            <div style={{ display:'flex', gap:8, marginTop:8 }}>
              <button style={btn} onClick={() => { setSpecialConfirm(null); save('special', { special_hours: draft.special }); }}>Yes, save</button>
              <button className="cal-post-btn cal-post-btn-ghost" onClick={() => setSpecialConfirm(null)}>Cancel</button>
            </div>
          </div>
        )}
        <SaveResult result={results.special} />
      </div>

      {/* Photos */}
      <div style={card}>
        <h3 style={{ margin:'0 0 12px', fontSize:15, color:'var(--text-primary)' }}>Photos on your listing</h3>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:12 }}>
          <select aria-label="Photo type" style={{ ...input, width:'auto' }} value={photoType} onChange={e => setPhotoType(e.target.value)}>
            {PHOTO_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <label style={{ ...btn, display:'inline-block', opacity: busy === 'photo' ? 0.6 : 1 }}>
            {busy === 'photo' ? 'Adding…' : 'Add a photo'}
            <input type="file" accept="image/jpeg,image/png" aria-label="Photo file" style={{ display:'none' }} disabled={busy === 'photo'}
              onChange={e => { addPhoto(e.target.files && e.target.files[0]); e.target.value = ''; }} />
          </label>
        </div>
        <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:10, lineHeight:1.5 }}>
          JPG or PNG, at least 250 pixels on the short side. Google may take a little while to show a new photo.
        </div>
        {photoResult && photoResult.error && <div className="cq-error" style={{ marginBottom:8 }}>{photoResult.error}</div>}
        {photoResult && photoResult.ok && <div style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:8 }} data-testid="photo-added">Photo sent to Google.</div>}
        {photos === null && <div style={{ fontSize:13, color:'var(--text-muted)' }}>Loading photos…</div>}
        {photos && photos.length === 0 && <div style={{ fontSize:13, color:'var(--text-muted)' }}>No photos yet.</div>}
        {photos && photos.length > 0 && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(110px, 1fr))', gap:8 }}>
            {photos.map(p => (
              <a key={p.name || p.url} href={p.url} target="_blank" rel="noopener noreferrer" style={{ display:'block', position:'relative', aspectRatio:'1', borderRadius:8, overflow:'hidden', background:'#000' }}>
                {p.thumbnail && <img src={p.thumbnail} alt={PHOTO_TYPES.find(t => t[0] === p.category)?.[1] || 'Listing photo'} style={{ width:'100%', height:'100%', objectFit:'cover' }} />}
                <span style={{ position:'absolute', left:4, bottom:4, fontSize:10, background:'rgba(0,0,0,0.6)', color:'#fff', padding:'1px 6px', borderRadius:4 }}>
                  {PHOTO_TYPES.find(t => t[0] === p.category)?.[1] || 'Photo'}
                </span>
              </a>
            ))}
          </div>
        )}
      </div>

      <RecentChanges history={history} />
    </div>
  );
}
